import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styles from './ForgotPassword.module.css';
import InputField from '../../components/inputfield/InputField';
import CustomButton from '../../components/buttons/CustomButton';
import LogoImage from '../../assets/images/logo-full.png';
import VisibilityOnIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import { ResetPasswordAPI } from '../../api/API';
import { clearAccessToken } from '../../api/authTokens';
import Loading from '../../components/loading/Loading';

function ResetPassword() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [token, setToken] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const inputFieldPasswordRef = useRef(null);
  const inputFieldConfirmPasswordRef = useRef(null);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Basic validation to ensure password is not empty
  const validatePassword = (password) => {
    if (!password) {
      return 'A senha é obrigatória';
    }
    return null;
  };

  const validatePasswords = () => {
    const password = inputFieldPasswordRef.current.GetValue();
    const confirmPassword = inputFieldConfirmPasswordRef.current.GetValue();
    
    // Only check if passwords are not empty and match
    const passwordError = validatePassword(password);
    if (passwordError) {
      inputFieldPasswordRef.current.SetError(passwordError);
      return false;
    }

    if (!confirmPassword) {
      inputFieldConfirmPasswordRef.current.SetError('A confirmação de senha é obrigatória');
      return false;
    }
    
    if (password != confirmPassword) {
      inputFieldConfirmPasswordRef.current.SetError('As senhas não coincidem');
      return false;
    }
    return true;
  };

  const handleResetPassword = () => {
    if (validatePasswords()) {
      setLoading(true);
      setErrorMessage(null);
      
      const password = inputFieldPasswordRef.current.GetValue();
      
      ResetPasswordAPI(token, password, (response) => {
        setLoading(false);
        
        if (response.error) {
          // Display the error from the backend
          setErrorMessage(response.error);
          
          // Set the error on the password field
          if (inputFieldPasswordRef.current) {
            inputFieldPasswordRef.current.SetError(response.error);
          }
          return;
        }
        
        // Clear all session data and cache again before redirecting
        clearAccessToken();
        localStorage.removeItem('userData');
        // Mantém o email lembrado se o usuário optou por isso
        // localStorage.removeItem('rememberedEmail');
        // localStorage.removeItem('rememberMe');
        sessionStorage.clear();
        
        // Clear URL parameters
        if (window.history && window.history.replaceState) {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
        
        // Set a flag to indicate we're coming from a password reset
        sessionStorage.setItem('from_password_reset', 'true');
        
        setResetSuccess(true);
        setCountdown(3);
      });
    }
  };
  
  // Handle countdown and redirect
  useEffect(() => {
    let countdownInterval;
    
    if (resetSuccess && countdown > 0) {
      countdownInterval = setInterval(() => {
        setCountdown(prevCount => {
          if (prevCount <= 1) {
            clearInterval(countdownInterval);
            navigate('/login', { replace: true });
            return 0;
          }
          return prevCount - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [resetSuccess, countdown, navigate]);

  // Check for token in URL when component mounts
  useEffect(() => {
    // Clear all session data and cache
    clearAccessToken();
    localStorage.removeItem('userData');
    // Mantém o email lembrado se o usuário optou por isso
    // localStorage.removeItem('rememberedEmail');
    // localStorage.removeItem('rememberMe');
    sessionStorage.clear();
    
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    
    if (!urlToken) {
      setErrorMessage('Token de redefinição não encontrado. Por favor, solicite uma nova redefinição de senha.');
      return;
    }
    
    setToken(urlToken);
  }, []);
  
  // Function to redirect immediately
  const redirectToLogin = () => {
    navigate('/login', { replace: true });
  };

  return (
    <div className={styles.container}>
      {loading ? <Loading /> : (
      <div className={styles.containerForm}>
        <div className={styles.logoContainer}>
          <img className={styles.logo} src={LogoImage} alt="Logo" />
          <h1 className={styles.title}>Nova Senha</h1>
          <p className={styles.subtitle}>
            Digite sua nova senha
          </p>
        </div>
        
        {errorMessage && (
          <div className={styles.errorMessage}>
            {errorMessage}
          </div>
        )}
        
        {resetSuccess && (
          <div className={styles.successMessage}>
            <strong>Senha alterada com sucesso!</strong>
            <p>Você será redirecionado em {countdown} segundos.</p>
            <div style={{ marginTop: '16px' }}>
              <CustomButton
                variant="contained"
                style="primary"
                label="Ir para login agora"
                onClick={redirectToLogin}
              />
            </div>
          </div>
        )}

        {!resetSuccess && (
          <>
            <div style={{ position: 'relative' }}>
              <InputField
                Width={"100%"}
                Label={"Nova Senha"}
                type={showPassword ? "text" : "password"}
                placeholder="Digite sua nova senha"
                ref={inputFieldPasswordRef}
                disabled={resetSuccess}
              />
              {!showPassword ? (
                <VisibilityOnIcon
                  className={styles.buttonVisibility}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                  onClick={handleTogglePasswordVisibility}
                />
              ) : (
                <VisibilityOffIcon
                  className={styles.buttonVisibility}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                  onClick={handleTogglePasswordVisibility}
                />
              )}
            </div>

            <div style={{ position: 'relative' }}>
              <InputField
                Width={"100%"}
                Label={"Confirmar Nova Senha"}
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirme sua nova senha"
                ref={inputFieldConfirmPasswordRef}
                disabled={resetSuccess}
              />
              {!showConfirmPassword ? (
                <VisibilityOnIcon
                  className={styles.buttonVisibility}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                  onClick={handleToggleConfirmPasswordVisibility}
                />
              ) : (
                <VisibilityOffIcon
                  className={styles.buttonVisibility}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                  onClick={handleToggleConfirmPasswordVisibility}
                />
              )}
            </div>

            <CustomButton
              variant="contained"
              style="primary"
              label={"Alterar Senha"}
              onClick={handleResetPassword}
            />
          </>
        )}
        {!token && (
          <div className={styles.backLink}>
            <Link to="/forgot-password">Voltar para recuperação de senha</Link>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

export default ResetPassword;